import { ArrowDown } from 'lucide-react';
import { Chip } from '@/components/ui/chip';

/**
 * `card_moved` breadcrumb chip (spec 004-p6, T017).
 *
 * A thin wrapper over the existing `ui/chip.tsx` primitive (R12 — reuse, not a
 * net-new component). When a materialized roster card re-anchors at the
 * conversation frontier, `applyWidgetReplace` leaves this chip where the card
 * used to be; tapping it scrolls to the live card.
 *
 * Accessibility (FR-013): the chip announces politely (`aria-live="polite"`) so a
 * screen-reader user hears that the card moved without a focus jump, and the
 * scroll honours `prefers-reduced-motion` (instant, not smooth, when reduced
 * motion is requested).
 *
 * Renders through the widget registry (`WidgetRenderer`) — the marker rides the
 * standard `widget` block, so it needs no bespoke transcript wiring.
 */
export interface CardMovedChipProps {
  /** `widget_id` of the live card this chip points at (the scroll target). */
  widget_id: string;
  /** The turn the live card re-anchored into (informational). */
  moved_to_turn?: string;
  /** Copy override; defaults to a neutral "moved" line. */
  label?: string;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function CardMovedChip({ widget_id, label }: CardMovedChipProps) {
  const scrollToLiveCard = () => {
    if (typeof document === 'undefined') return;
    const target = document.querySelector<HTMLElement>(`[data-widget-id="${CSS.escape(widget_id)}"]`);
    if (!target || typeof target.scrollIntoView !== 'function') return;
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });
  };

  return (
    <div aria-live="polite" className="flex">
      <Chip
        variant="info"
        size="sm"
        role="button"
        tabIndex={0}
        data-testid="card-moved-chip"
        className="cursor-pointer gap-1 hover:brightness-105"
        onClick={scrollToLiveCard}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scrollToLiveCard();
          }
        }}
      >
        <ArrowDown className="h-3 w-3" aria-hidden="true" />
        {label ?? 'Card updated below'}
      </Chip>
    </div>
  );
}
